import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const texts = {
  de: {
    badge: 'Newsletter',
    title: 'Alle 6 Wochen ein KI-Use-Case aus deinem Alltag.',
    sub: 'Pro Ausgabe: ein konkretes Beispiel + die Technologie dahinter + ehrliche Aufwands-Einschätzung + Quellen. Kein Bullshit, keine Buzzwords.',
    namePlaceholder: 'Vorname',
    emailPlaceholder: 'E-Mail-Adresse',
    button: 'Anmelden',
    sending: 'Sende...',
    marketingConsent: 'Ich möchte den ENBET Newsletter und gelegentlich relevante Informationen zum Thema KI im Handel per E-Mail erhalten.',
    termsConsent: 'Ich akzeptiere die',
    termsLink: 'AGB',
    and: 'und',
    privacyLink: 'Datenschutzerklärung',
    consentNote: 'Du kannst dich jederzeit mit einem Klick wieder abmelden.',
    success: 'Fast geschafft!',
    successText: 'Wir haben dir eine Bestätigungs-E-Mail geschickt. Klick einfach auf den Link darin — dann bist du dabei.',
    error: 'Etwas ist schiefgelaufen',
  },
  en: {
    badge: 'Newsletter',
    title: 'One AI use case from your daily business — every 6 weeks.',
    sub: 'Per issue: a concrete example + the technology behind it + honest effort estimation + sources. No bullshit, no buzzwords.',
    namePlaceholder: 'First name',
    emailPlaceholder: 'Email address',
    button: 'Subscribe',
    sending: 'Sending...',
    marketingConsent: 'I want to receive the ENBET newsletter and occasional relevant information about AI in retail by email.',
    termsConsent: 'I accept the',
    termsLink: 'Terms',
    and: 'and',
    privacyLink: 'Privacy Policy',
    consentNote: 'You can unsubscribe at any time with a single click.',
    success: 'Almost there!',
    successText: 'We sent you a confirmation email. Just click the link inside — then you\'re in.',
    error: 'Something went wrong',
  },
};

export default function NewsletterSignup() {
  const { lang } = useLanguage();
  const t = texts[lang];

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !marketingConsent || !termsAccepted) return;
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/public/newsletter/subscribe`, {
        email: email.trim(),
        name: name.trim(),
        source: 'homepage',
        marketing_consent: marketingConsent,
        terms_accepted: termsAccepted,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    }
    setLoading(false);
  };

  const openTerms = (e) => { e.preventDefault(); window.dispatchEvent(new Event('openAGB')); };
  const openPrivacy = (e) => { e.preventDefault(); window.dispatchEvent(new Event('openDatenschutz')); };

  return (
    <section data-testid="newsletter-section" className="bg-[#0a0a0a] py-20 md:py-24">
      <div className="max-w-[800px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-4 text-center" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t.badge}
          </p>
          <h2 className="text-white text-3xl md:text-4xl font-bold uppercase tracking-tight leading-[1.3] mb-4 text-center" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t.title}
          </h2>
          <p className="text-white/60 text-base text-center max-w-xl mx-auto mb-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {t.sub}
          </p>

          {success ? (
            <div className="text-center max-w-md mx-auto">
              <div className="w-1.5 h-12 bg-[#befa0a] mx-auto mb-5" />
              <h3 className="text-white text-2xl font-bold uppercase mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t.success}
              </h3>
              <p className="text-white/60 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {t.successText}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="max-w-md mx-auto space-y-3">
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t.namePlaceholder}
                className="w-full bg-white/5 border border-white/15 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] placeholder-white/30 rounded"
                style={{ fontFamily: "'Manrope', sans-serif" }} />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.emailPlaceholder} required
                className="w-full bg-white/5 border border-white/15 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#befa0a] placeholder-white/30 rounded"
                style={{ fontFamily: "'Manrope', sans-serif" }} />

              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input type="checkbox" checked={marketingConsent} onChange={e => setMarketingConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#befa0a] flex-shrink-0" />
                <span className="text-white/70 text-xs leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {t.marketingConsent}
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#befa0a] flex-shrink-0" />
                <span className="text-white/70 text-xs leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {t.termsConsent}{' '}
                  <button type="button" onClick={openTerms} className="text-[#befa0a] hover:underline">{t.termsLink}</button>
                  {' '}{t.and}{' '}
                  <button type="button" onClick={openPrivacy} className="text-[#befa0a] hover:underline">{t.privacyLink}</button>.
                </span>
              </label>

              {error && <p className="text-red-400 text-sm text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>{error}</p>}
              <button type="submit" disabled={loading || !marketingConsent || !termsAccepted}
                className="w-full bg-[#befa0a] text-[#0a0a0a] py-3 font-bold uppercase tracking-wider rounded hover:bg-lime-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {loading ? t.sending : t.button}
              </button>
              <p className="text-white/40 text-[11px] text-center pt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {t.consentNote}
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
